/**
 * P0-DOB-ONBOARDING-01 — post-authentication identity completion.
 *
 * Google and Apple never return a date of birth, and patients who
 * registered before the 18+ policy have none either, so without this the
 * requirement is first discovered at offer acceptance or payment. This
 * app-shell gate asks for it immediately after sign-in instead.
 *
 * It is a UX gate, not a security control: the canonical server-side
 * guard (`assert_patient_transactional_eligible`) still blocks Request /
 * Accept / Book / Pay, so refreshing, deep-linking or switching login
 * method cannot buy an underage user anything. It reads the SAME
 * canonical pre-flight (`GET /api/patient/profile/eligibility`) and writes
 * through the SAME canonical writer as every other entry point.
 *
 * Never loops: it asks once per identity per browser session, closes for
 * good on a successful save, and "later" is remembered for the session.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../utils/api';
import { DateOfBirthGate } from './DateOfBirthGate';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const SNOOZE_KEY = 'treatx_dob_completion_snoozed';

const isSnoozed = (identity) => {
  try {
    return sessionStorage.getItem(SNOOZE_KEY) === identity;
  } catch {
    return false;
  }
};

export function PatientIdentityCompletionInterceptor() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const checkedFor = useRef(null);

  const identity = user?.id && (user.role === 'patient' || user.user_type === 'patient')
    ? `${user.id}:patient`
    : null;

  const check = useCallback(async () => {
    if (!identity) {
      checkedFor.current = null;
      setState(null);
      setOpen(false);
      return;
    }
    if (checkedFor.current === identity) return;
    checkedFor.current = identity;
    try {
      const { data } = await api.get('/patient/profile/eligibility');
      if (checkedFor.current !== identity) return;
      setState(data);
      const needsDob = data && data.eligible === false && data.date_of_birth_present === false;
      const blockedByAge = data?.code === 'PATIENT_UNDER_18';
      setOpen(Boolean((needsDob || blockedByAge) && !isSnoozed(identity)));
    } catch {
      // Never block the app shell on this read — the server-side guard
      // still protects every transaction.
      if (checkedFor.current !== identity) return;
      setState(null);
      setOpen(false);
    }
  }, [identity]);

  useEffect(() => { check(); }, [check]);

  if (!identity || !open || !state) return null;

  const blockedByAge = state.code === 'PATIENT_UNDER_18';

  const later = () => {
    try { sessionStorage.setItem(SNOOZE_KEY, identity); } catch { /* private mode */ }
    setOpen(false);
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) later(); }}>
      <DialogContent className="max-w-lg" data-testid="dob-completion-dialog">
        <DialogHeader>
          <DialogTitle data-testid="dob-completion-title">
            {blockedByAge ? t('patient.dob.restrictedTitle') : t('patient.dob.completionTitle')}
          </DialogTitle>
          <DialogDescription data-testid="dob-completion-subtitle">
            {blockedByAge ? t('patient.dob.restrictedBody') : t('patient.dob.completionBody')}
          </DialogDescription>
        </DialogHeader>
        <DateOfBirthGate
          code={state.code}
          message={blockedByAge ? state.message : t('patient.dob.explain')}
          testIdPrefix="dob-completion"
          onSaved={() => {
            setOpen(false);
            setState({ eligible: true, date_of_birth_present: true });
          }}
        />
        {!blockedByAge && (
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={later} data-testid="dob-completion-later">
              {t('patient.dob.later')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PatientIdentityCompletionInterceptor;
